import { Dashboard, ErrorResponse, ServiceResponse } from '@bzbs/react-api-client';
import { create } from 'zustand';
import { useBuzzebeesAppStore } from './buzzebeess-app.store';
import { useLocaleStore } from './locale.store';

export interface DashboardConfig {
  defaultDashboardMode?: 'main' | 'sub';
  defaultDashboardConfig?: string;
  appId?: string;
}

export type DashboardState = {
  data: Dashboard[];
  isLoading: boolean;
  error: ErrorResponse | null;
  config: DashboardConfig;
  options?: {
    [key: string]: unknown;
  };
};

export type DashboardActions = {
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: ErrorResponse) => void;
  fetchData: (options?: { [key: string]: unknown }) => Promise<ServiceResponse<Dashboard[]>>;
  clear: () => void;
  updateConfig: (config: Partial<DashboardConfig>) => void;
};

export type DashboardStore = DashboardState & DashboardActions;

export const createDashboardStore = (initialConfig: DashboardConfig) => {
  return create<DashboardStore>((set, get) => {
    return {
      data: [],
      isLoading: false,
      error: null,
      config: initialConfig,
      options: {},

      setIsLoading: (isLoading: boolean) => set({ isLoading }),
      setError: (error: ErrorResponse) => set({ error }),

      updateConfig: (newConfig: Partial<DashboardConfig>) => {
        set((state) => ({
          config: { ...state.config, ...newConfig },
        }));
      },

      fetchData: async (options?: { [key: string]: unknown }) => {
        const { config } = get();
        const { defaultDashboardMode, defaultDashboardConfig, appId } = config;
        const bzbsService = useBuzzebeesAppStore.getState().bzbsService;
        const localeId = useLocaleStore.getState().localeId;

        if (!bzbsService) {
          return { type: 'client-error', message: 'Service not configured' };
        }

        set({ options: options || {} });

        set({ isLoading: true, error: null });
        let response: ServiceResponse<Dashboard[]>;
        if (defaultDashboardMode === 'main') {
          if (!appId) {
            return { type: 'client-error', message: 'App ID not configured' };
          }
          response = await bzbsService.dashboardApi.mainDashboard({
            appName: appId,
            locale: localeId,
            options: { device_locale: localeId, ...options },
          });
        } else if (defaultDashboardMode === 'sub' && defaultDashboardConfig) {
          response = await bzbsService.dashboardApi.subDashboard({
            dashboardName: defaultDashboardConfig,
            locale: localeId,
            options: { device_locale: localeId, ...options },
          });
        } else {
          if (!defaultDashboardConfig) {
            return { type: 'client-error', message: 'Dashboard config not configured' };
          }
          response = await bzbsService.dashboardApi.subDashboard({
            dashboardName: defaultDashboardConfig,
            locale: localeId,
            options: { device_locale: localeId, ...options },
          });
        }
        set({ isLoading: false, error: null });

        if (response.type === 'success') {
          set({ data: response.model });
        } else {
          set({ error: response });
        }
        return response;
      },

      clear: () => {
        set({
          data: [],
          isLoading: false,
          error: null,
        });
      },
    };
  });
};
